import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ShieldCheck, Smartphone, Users, PiggyBank } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/format";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const demoMembers = [
  { label: "Admin · Haresh", mobile: "9315341037", otp: "1311" },
  { label: "Ramesh Yadav", mobile: "9999999999", otp: "1234" },
  { label: "Rajesh Kumar", mobile: "9812345678", otp: "1234" },
  { label: "Sunita Patel", mobile: "9898989898", otp: "1234" },
];

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const m = await login(mobile.trim(), otp.trim());
      toast.success(`नमस्ते ${m.name.split(" ")[0]}!`);
      navigate("/");
    } catch (err) {
      toast.error(apiError(err, "Login fail hua"));
    } finally {
      setLoading(false);
    }
  };

  const quickFill = (mob) => {
    setMobile(mob);
    const f = demoMembers.find((d) => d.mobile === mob);
    setOtp(f ? f.otp : "1234");
  };

  const sendOtp = async () => {
    if (!mobile.trim()) return toast.error("पहले मोबाइल नंबर दर्ज करें");
    try {
      const { data } = await api.post("/auth/send-otp", { mobile: mobile.trim() });
      if (data.simulated) {
        if (data.demo_otp) setOtp(data.demo_otp);
        toast.success(`WhatsApp OTP भेजा गया (डेमो): ${data.demo_otp || ""}`);
      } else {
        toast.success("WhatsApp पर OTP भेज दिया गया ✓");
      }
    } catch (e) {
      toast.error(apiError(e, "OTP भेजने में समस्या"));
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-primary text-white p-12 relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
        <div className="flex items-center gap-3 relative">
          <div className="h-11 w-11 rounded-xl bg-white/15 flex items-center justify-center">
            <Users className="h-6 w-6" />
          </div>
          <span className="text-xl font-display font-bold">अपना समूह</span>
        </div>
        <div className="relative space-y-6">
          <h1 className="text-4xl xl:text-5xl font-display font-extrabold leading-tight">
            समूह • सुरक्षित • सरल
          </h1>
          <p className="text-white/80 text-base max-w-md leading-relaxed">
            अपने स्वयं सहायता समूह का हिसाब एक ही जगह — जमा, ऋण, सत्यापन और KYC। Members apna balance dekhein, admin poora control rakhe.
          </p>
          <div className="flex gap-6 pt-4">
            {[
              { icon: ShieldCheck, t: "Privacy First" },
              { icon: PiggyBank, t: "UPI Jama" },
              { icon: Smartphone, t: "OTP Login" },
            ].map((f) => (
              <div key={f.t} className="flex items-center gap-2 text-sm text-white/90">
                <f.icon className="h-4 w-4" />
                {f.t}
              </div>
            ))}
          </div>
        </div>
        <p className="text-xs text-white/50 relative">Apna Samooh v2.1 · ₹ INR</p>
      </div>

      {/* Login form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="h-10 w-10 rounded-xl bg-primary flex items-center justify-center">
              <Users className="h-5 w-5 text-white" />
            </div>
            <span className="text-lg font-display font-bold">अपना समूह</span>
          </div>

          <h2 className="text-2xl font-display font-bold tracking-tight">लॉगिन करें</h2>
          <p className="text-muted-foreground text-sm mt-1 mb-8">
            मोबाइल नंबर और OTP दर्ज करें। (डेमो OTP: Member <span className="font-semibold text-primary">1234</span>)
          </p>

          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="mobile">मोबाइल नंबर / Mobile</Label>
              <Input id="mobile" data-testid="login-mobile-input" inputMode="numeric" placeholder="98xxxxxxxx" value={mobile} onChange={(e) => setMobile(e.target.value)} className="h-12 font-mono" required />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="otp">OTP</Label>
                <button type="button" data-testid="send-otp-btn" onClick={sendOtp} className="text-xs font-semibold text-primary">
                  OTP भेजें (Free WhatsApp) ›
                </button>
              </div>
              <Input id="otp" data-testid="login-otp-input" inputMode="numeric" placeholder="1234" value={otp} onChange={(e) => setOtp(e.target.value)} className="h-12 font-mono tracking-[0.4em]" required />
            </div>
            <Button type="submit" data-testid="login-submit-btn" disabled={loading} className="w-full h-12 text-base font-semibold">
              {loading ? "Verify ho raha hai…" : "लॉगिन करें"}
            </Button>
          </form>

          <div className="mt-8">
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Quick demo login</p>
            <div className="grid grid-cols-2 gap-2">
              {demoMembers.map((d) => (
                <button key={d.mobile} data-testid={`demo-login-${d.mobile}`} onClick={() => quickFill(d.mobile)} className="text-left px-3 py-2 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition-colors">
                  <span className="block text-sm font-medium">{d.label}</span>
                  <span className="block text-xs font-mono text-muted-foreground">{d.mobile}</span>
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
